import styled from "@emotion/styled/macro";
import { useEffect, useRef, useState } from "react";
import {
  Modal,
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
} from "@sunrun/experience-ui-components";
import {
  ReviewOffer,
  ReviewOfferProduct,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { CardCarouselData } from "providers/Types";
import { SystemDataOverlay } from "components/atoms/SystemDataOverlay";
import {
  maxPowerCardData,
  storageCapacityCardData,
} from "components/atoms/UsageOffsetCardData";
import { getTitleTabScrollLeft } from "utils/productCarouselTabScroll";
import { sortProductsByDisplayOrder } from "utils/sortProducts";
import { getBatteryCarouselLabel } from "utils/batteryProductLabels";
import { PRODUCT_DISPLAY_ORDER } from "constants/bundleAndProductIds";

type ProductCarouselProps = {
  products: ReviewOfferProduct[];
  offer?: ReviewOffer;
};

const BATTERY_IDS = new Set<string>(PRODUCT_DISPLAY_ORDER.BATTERIES);

const ProductCarousel = ({ products, offer }: ProductCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showProductImageModal, setShowProductImageModal] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const titleScrollRef = useRef<HTMLDivElement>(null);

  const sortedProducts = sortProductsByDisplayOrder(products);

  useEffect(() => {
    const scrollContainer = titleScrollRef.current;
    const activeTab = tabRefs.current[currentIndex];
    if (!scrollContainer || !activeTab) {
      return;
    }

    const nextScrollLeft = getTitleTabScrollLeft(scrollContainer, activeTab);

    if (nextScrollLeft === scrollContainer.scrollLeft) {
      return;
    }

    scrollContainer.scrollTo({
      left: nextScrollLeft,
      behavior: "smooth",
    });
  }, [currentIndex, sortedProducts.length]);

  const data = [
    storageCapacityCardData(offer),
    maxPowerCardData(offer),
  ] as CardCarouselData[];
  const currentProduct = sortedProducts[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === sortedProducts.length - 1;

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (!isLast) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleExpandImage = () => {
    setShowProductImageModal(true);
  };

  const productLabel = (product: ReviewOfferProduct) => {
    if (product.productId === PRODUCT_DISPLAY_ORDER.SOLAR) {
      return `${product.count} Solar Panel${product.count === 1 ? "" : "s"}`;
    }

    return getBatteryCarouselLabel(product);
  };

  return (
    <CarouselContainer>
      <TopNavButtons>
        <TopNavButtonsScroll
          ref={titleScrollRef}
          data-testid="product-carousel-title-scroll"
        >
          {sortedProducts.map((product, index) => (
            <TopNavButton
              key={`${product.productId ?? "product"}-${index}`}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              type="button"
              onClick={() => setCurrentIndex(index)}
              $isCurrent={index === currentIndex}
              aria-label={productLabel(product)}
              aria-current={index === currentIndex ? "true" : undefined}
            >
              {productLabel(product)}
            </TopNavButton>
          ))}
        </TopNavButtonsScroll>
      </TopNavButtons>
      <CarouselContent data-testid="product-carousel-image">
        <CarouselSlideTrack
          $count={sortedProducts.length}
          $index={currentIndex}
        >
          {sortedProducts.map((product, index) => (
            <CarouselSlide
              key={`slide-${product.productId}`}
              $count={sortedProducts.length}
            >
              <CarouselImage
                bgImageUrl={product.imageUrl}
                role="img"
                aria-hidden={index !== currentIndex}
                aria-label={product.name}
              />
            </CarouselSlide>
          ))}
        </CarouselSlideTrack>
      </CarouselContent>
      {sortedProducts.length > 1 && (
        <>
          <ArrowButton
            type="button"
            onClick={handlePrev}
            disabled={isFirst}
            aria-label="Previous product"
            $position="left"
          >
            <SvgIcon
              name={SvgNames.ChevronLeft}
              width={24}
              height={24}
              color={tokens.WHITE}
            />
          </ArrowButton>
          <ArrowButton
            type="button"
            onClick={handleNext}
            disabled={isLast}
            aria-label="Next product"
            $position="right"
          >
            <SvgIcon
              name={SvgNames.ChevronRight}
              width={24}
              height={24}
              color={tokens.WHITE}
            />
          </ArrowButton>
        </>
      )}

      <CarouselBottomSection>
        <ExpandImageButton
          onClick={handleExpandImage}
          aria-label={`View ${currentProduct.name}`}
          $inSection
        >
          <SvgIcon
            name={SvgNames.ArrowsOutSimple}
            width={18}
            height={18}
            color={tokens.BRAND_HERO_BLUE}
          />
        </ExpandImageButton>
        {data.length > 0 && BATTERY_IDS.has(currentProduct.productId) && (
          <SystemDataOverlay data={data} variant="inline" />
        )}
      </CarouselBottomSection>
      {showProductImageModal && (
        <ProductImageModal
          onClose={() => setShowProductImageModal(false)}
          testId="product-carousel-image-modal"
        >
          <ProductImageModalContainer>
            <ProductImageModalImage
              src={currentProduct.imageUrl}
              alt={currentProduct.name}
            />
          </ProductImageModalContainer>
        </ProductImageModal>
      )}
    </CarouselContainer>
  );
};

const CarouselContainer = styled.div`
  position: relative;
  width: 373px;
  height: 440px;
  border-radius: 10px;
  /* Reversed NIGHT_BLACK_HERO: black at top, #1F2647 at bottom */
  background: linear-gradient(180deg, ${tokens.BLACK} 0%, #1f2647 100%);
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    width: 100%;

    /* Fluid height: 336px at 375px viewport → 440px at 768px viewport */
    --carousel-height-min: 336px;
    --carousel-height-max: 440px;
    --carousel-vw-min: 375px;
    --carousel-vw-max: 768px;
    --carousel-vw-range: 393px; /* 768 - 375 */
    --carousel-height-range: 104px; /* 440 - 336 */
    height: clamp(
      var(--carousel-height-min),
      calc(
        var(--carousel-height-min) + (100vw - var(--carousel-vw-min)) *
          var(--carousel-height-range) / var(--carousel-vw-range)
      ),
      var(--carousel-height-max)
    );
  }
`;

const TopNavButtons = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
  padding: 14px 14px 20px;
  z-index: 1;
`;

const TopNavButtonsScroll = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding-inline: 14px;
  scroll-padding-inline: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TopNavButton = styled.button<{ $isCurrent: boolean }>`
  flex-shrink: 0;
  background: none;
  border: none;
  border-bottom: ${({ $isCurrent }) =>
    $isCurrent ? `2px solid ${tokens.WHITE}` : "2px solid transparent"};
  color: ${tokens.WHITE};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

/**
 * Horizontal carousel layout (track + translateX percentages are relative to the track):
 * - Track width = N × viewport → N slides sit side by side, each slide = 1/N of the track.
 * - translateX(-(index/N)×100%) moves the track left by `index` full viewports.
 */
const trackWidthPercent = (slideCount: number) => slideCount * 100;

const trackOffsetPercent = (activeIndex: number, slideCount: number) =>
  (-activeIndex / slideCount) * 100;

/** Each slide spans this fraction of the track (so N slides fill 100% of the track = one viewport each). */
const slideFlexBasisPercent = (slideCount: number) => 100 / slideCount;

const CarouselContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const CarouselSlideTrack = styled.div<{ $count: number; $index: number }>`
  display: flex;
  height: 100%;
  width: ${({ $count }) => trackWidthPercent($count)}%;
  transform: translateX(
    ${({ $index, $count }) => trackOffsetPercent($index, $count)}%
  );
  transition: transform 0.38s cubic-bezier(0.33, 1, 0.68, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CarouselSlide = styled.div<{ $count: number }>`
  flex: 0 0 ${({ $count }) => slideFlexBasisPercent($count)}%;
  height: 100%;
  min-width: 0;
`;

const CarouselImage = styled.div<{ bgImageUrl: string }>`
  width: 100%;
  height: 100%;
  background: url(${({ bgImageUrl }) => bgImageUrl}) no-repeat center;
  background-size: contain;
  transform: scale(0.7);
`;

const CarouselBottomSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 40px; // Aligns with solar card expand button
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0) 85%
  );
`;

const ExpandImageButton = styled.button<{ $inSection?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${tokens.WHITE};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  align-self: ${({ $inSection }) => ($inSection ? "flex-end" : "auto")};
  position: ${({ $inSection }) => ($inSection ? "static" : "absolute")};
  bottom: ${({ $inSection }) => ($inSection ? "auto" : "14px")};
  right: ${({ $inSection }) => ($inSection ? "auto" : "14px")};

  &:hover {
    opacity: 0.9;
  }
`;

const ArrowButton = styled.button<{ $position: "left" | "right" }>`
  position: absolute;
  ${({ $position }) => ($position === "left" ? "left: 14px;" : "right: 14px;")}
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ProductImageModal = styled(Modal)`
  > .modal-container {
    height: 90%;
    width: 90%;
    overflow: scroll;

    > #modal-header {
      padding-bottom: 0px;
    }

    > .modal-content-container {
      max-height: none;
    }
  }
`;

const ProductImageModalContainer = styled.div`
  position: relative;
  width: 100%;
  height: 65vh;
  margin: auto;
  padding-top: 10px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.NIGHT_BLACK_HERO};
`;

const ProductImageModalImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export { ProductCarousel };
